function generateInsertList(nodes)
{
  if(nodes.length==1)
    return typeof nodes[0]==='string' ? document.createTextNode(nodes[0]) : nodes[0];

  let frag = document.createDocumentFragment();
  nodes.forEach(node => frag.appendChild(typeof node === 'string' ? document.createTextNode(node) : node));
  return frag;
}

export function matches(node, selector)
{
  var tocall = node.matches || node.matchesSelector || node.msMatchesSelector || node.webkitMatchesSelector;
  //if none of the 'matches' was found, this might be a textnode or something. returning false is probably safest
  return tocall && tocall.apply(node, [selector]);
}
export function closest(node, selector)
{
  if(node.closest)
    return node.closest(selector);
  for(;node&&!matches(node,selector);node=node.parentElement)
    /*iterate*/;
  return node;
}
//implements contains. TODO we only really need this on IE11, which doesn't consider a text node a child, we can probably fall back to native elsewhere ?
export function contains(ancestor, child)
{
  for(;child;child=child.parentNode)
    if(child===ancestor)
      return true;
  return false;
}
//insert a range of nodes before a node: https://dom.spec.whatwg.org/#dom-childnode-before
export function before(node,...nodes)
{
  if(node.before)
  {
    node.before(...nodes);
    return;
  }
  if(node.parentNode)
    node.parentNode.insertBefore(generateInsertList(nodes), node);
}
//insert a range of nodes after a node: https://dom.spec.whatwg.org/#dom-childnode-after
export function after(node,...nodes)
{
  if(node.after)
  {
    node.after(...nodes);
    return;
  }
  if(node.parentNode)
    node.parentNode.insertBefore(generateInsertList(nodes), node.nextSibling);
}
//replace node with a set of nodes : https://dom.spec.whatwg.org/#dom-childnode-replacewith
export function replaceWith(node,...nodes)
{
  if(node.replaceWith)
  {
    node.replaceWith(...nodes);
    return;
  }
  if(node.parentNode)
    node.parentNode.replaceChild(generateInsertList(nodes), node);
}
//remove node with a set of nodes : https://dom.spec.whatwg.org/#dom-childnode-remove
export function remove(node)
{
  if(node.parentNode)
    node.parentNode.removeChild(node);
}
//insert nodes at start: https://dom.spec.whatwg.org/#dom-parentnode-prepend
export function prepend(node, ...nodes)
{
  node.insertBefore(generateInsertList(nodes), node.firstChild);
}
//insert nodes at end: https://dom.spec.whatwg.org/#dom-parentnode-append
export function append(node, ...nodes)
{
  node.appendChild(generateInsertList(nodes));
}

/* create elements. sets properties (not attributes!) immediately.
   everything inside 'on' is added as an addEventListener without capture
   everything inside 'childNodes' is appended to the node. nulls are ignored inside childNodes

   Examples:
   domtools.create("input", { type:"file", className: "myupload", style: { display: "none" }));

*/
export function create(elementname, attributes)
{
  var node = document.createElement(elementname);
  if(attributes)
    Object.keys(attributes).forEach(attrname =>
    {
      if(attrname == 'on') //create event listeners
      {
        Object.keys(attributes.on).forEach(eventname => node.addEventListener(eventname, attributes.on[eventname], false));
        return;
      }
      if(attrname == 'class')
        throw new Error("Use 'className' instead of 'class' in dompack.create");
      if(attrname == 'events')
        throw new Error("Use 'on' instead of 'events' in dompack.create");
      if(attrname == 'styles')
        throw new Error("Use 'style' instead of 'styles' in dompack.create");

      if (attrname == "className")
      {
        let value = attributes[attrname];
        if (typeof(value) === "object")
        {
          let obj = value;
          value = "";
          Object.keys(obj).forEach((key, idx) => { if (obj[key]) value += (idx ? " " : "") + key; });
        }
        node.className = value;
        return;
      }
      if(attrname == 'style' || attrname == 'dataset') //explicitly assign
      {
        Object.assign(node[attrname], attributes[attrname]);
        return;
      }
      if(attrname == 'childNodes') //append as children
      {
        append(node, ...attributes.childNodes.filter(child => child != null && child !== true && child !== false));
        return;
      }
      node[attrname] = attributes[attrname];
    });
  return node;
}

/** Function to create for jsx, create elements directly (instead of virtual dom nodes).

    import * as dompack from 'dompack';

    /* @jsx dompack.jsxcreate *\/
    your code
*/
export function jsxcreate(elementname, attributes, ...childNodes)
{
  // Ensure attributes
  attributes = attributes || {};
  // Flatten childnodes arrays
  let parts = (attributes.childNodes || []).concat(childNodes);
  parts = parts.reduce((acc, elt) => Array.isArray(elt) ? acc.concat(elt) : (acc.push(elt), acc), []);
  // Create the element
  attributes.childNodes = parts;
  return create(elementname, attributes);
}

/** Toggle classes in a node
    @param node Node which classes to toggle
    @param toggles Object, all keys will be added/removed based on the truthyness of their values
*/
export function toggleClasses(node, toggles)
{
  if (typeof(toggles) !== "object")
    throw new Error("Expected an object with keys as classnames");
  Object.keys(toggles).forEach(key => node.classList[toggles[key] ? "add" : "remove"](key));
}

/* remove the contents of an existing node */
export function empty(node)
{
  if(node.empty)
  {
    node.empty();
    return;
  }

  //node.innerHTML=''; // this does NOT work for IE11, it destroys all nodes instead of unlinking them
  while(node.lastChild)
    node.removeChild(node.lastChild);
}

export function isDomReady()
{
  return document.readyState == "interactive" || document.readyState == "complete";
}

/* run the specified function 'on ready'. adds to DOMContentLoaded if dom is not ready yet. Exceptions from the ready handler will not be fatal to the rest of code execution */
export function onDomReady(callback)
{
  if(isDomReady())
  {
    try
    {
      callback();
    }
    catch(e)
    {
      console.error("Exception executing a domready handler");
      console.log(e,e.stack);
    }
  }
  else
    document.addEventListener("DOMContentLoaded", callback);
}

//parse JSON data, throw with more info on parse failure
export function getJSONAttribute(node, attributename)
{
  try
  {
    return JSON.parse(node.getAttribute(attributename));
  }
  catch(e)
  {
    console.error("JSON parse failure on attribute '" +attributename+ "' of node", node);
    throw e;
  }
}

/** Get the base URI of the current document. IE11 doesn't implement document.baseURI
    @param doc Document to query. Defaults to window.document
*/
export function getBaseURI(doc)
{
  if(!doc)
    doc=window.document;
  if(doc.baseURI)
    return doc.baseURI;

  let base = doc.querySelector('base');
  if(base && base.href)
    return base.href;
  return doc.URL;
}



// WEBPACK FOOTER //
// /opt/wh/whtree/~/dompack/src/tree.es